import RemoteDataController from './RemoteDataController';
import DataController from './DataController';
import {Platform} from 'react-native';
import {orderBy, intersection} from 'lodash';
import RNFS from './RNFS';

const findMostRecentDate = dateStrings =>
  new Date(Math.max(...dateStrings.map(date => new Date(date))));

export default {
  async findBooksToUpdate(previousBooksIndex, currentBooksIndex) {
    if (!Array.isArray(previousBooksIndex) || previousBooksIndex.length === 0) {
      console.log('Previous books index missing or invalid. Using current.');
      return currentBooksIndex;
    }

    const booksToUpdate = currentBooksIndex.filter(currentBook => {
      const previousBook = previousBooksIndex.find(
        prev => prev.ID === currentBook.ID,
      );

      const currentModifiedRaw = currentBook?.['Last Modified'];
      const previousModifiedRaw = previousBook?.['Last Modified'];

      if (!currentModifiedRaw) {
        // No date means we can't trust it was modified, so skip updating
        return false;
      }

      if (!previousModifiedRaw) {
        // No previous date, treat as new or updated
        return true;
      }

      const currentModified = new Date(currentModifiedRaw);
      const previousModified = new Date(previousModifiedRaw);

      // Ensure valid dates before comparing
      if (
        isNaN(currentModified.getTime()) ||
        isNaN(previousModified.getTime())
      ) {
        return false;
      }

      return currentModified > previousModified;
    });

    const booksToDelete = previousBooksIndex.filter(previousBook => {
      const currentBook = currentBooksIndex.find(
        current => current.ID === previousBook.ID,
      );
      return !currentBook;
    });

    for (const book of booksToDelete) {
      console.log('book to delete', book.ID);
      await DataController.deleteBookFile(book.ID);
    }

    console.log(
      'booksToUpdate',
      booksToUpdate.map(book => book.ID),
    );

    return booksToUpdate;
  },

  async getLocalBook(bookID) {
    const localBook = await DataController.getBookFile(bookID);

    if (localBook) {
      return localBook;
    }

    return null;
  },

  async updateBook(book) {
    const bookID = book.ID;
    console.log('updatingBook', bookID);
    try {
      const bookData = await RemoteDataController.downloadBookData(bookID);
      const bookFile = `${bookID}.json`;
      await DataController.saveBookFile(bookData, bookFile);

      const bookCoverImage = book?.['Cover Image'];

      if (Platform.OS !== 'web') {
        if (bookCoverImage) {
          const downloadCount = await RemoteDataController.downloadBookImages(
            bookID,
            bookCoverImage,
          );
          console.log('book cover image downloadCount', bookID, downloadCount);
        }

        if (book.Images.length > 0) {
          const downloadCount = await RemoteDataController.downloadBookImages(
            bookID,
            book.Images,
          );
          console.log('book image downloadCount', bookID, downloadCount);
        }
      }

      await RemoteDataController.downloadBookCss(bookID, book.CSS);
      return bookData;
    } catch (error) {
      console.log('error updating book: ' + bookID + error);
      const localBook = await this.getLocalBook(bookID);
      return localBook;
    }
  },

  async checkAndUpdateBooksIndex(currentBooksIndex, latestBooksIndex) {
    try {
      await DataController.updateBooksIndexFile(latestBooksIndex);
      return latestBooksIndex;
    } catch (error) {
      console.log('error getting booksIndex: ' + error);
      return currentBooksIndex;
    }
  },

  getBookImageFilePath(bookID, filename) {
    if (Platform.OS === 'web') {
      const path = `${bookID}/images/${filename}`;
      const url =
        `https://ppp-temp-files.s3.amazonaws.com/data/ppBooks/${path}`.replace(
          / /g,
          '%20',
        );
      return url;
    } else {
      const imagePath = DataController.getBookImagePath(filename, bookID);
      return imagePath;
    }
  },

  async processBooks(books) {
    try {
      const processedBooks = await Promise.all(
        books.map(async book => {
          try {
            const bookFileName = `${book.ID}.json`;
            const bookContents = await DataController.getBookFile(bookFileName);
            const cssSource = await DataController.getBookCssFile(book.ID);

            // Skip if no contents
            if (!bookContents || !bookContents.length) return null;

            const orderedContents = orderBy(
              bookContents,
              ['Chapter Number', 'Section Number'],
              ['asc', 'asc'],
            );

            const chapters = [];
            let previousSectionChapter = -1;

            for (const section of orderedContents) {
              const sectionName =
                section['Section Name']?.trim().replace(/\s+/g, ' ') || '';
              const sectionHtml =
                section['HTML']?.trim().replace(/\s+/g, ' ') || '';
              const sectionReadTime = section['Read Time'] || 0;

              const chapterNumber = section['Chapter Number'];
              const sectionNumber = section['Section Number'];

              if (chapterNumber == null || sectionNumber == null) continue;

              if (previousSectionChapter !== chapterNumber) {
                const chapterName =
                  section['Chapter Name']?.trim().replace(/\s+/g, ' ') || '';

                chapters.push({
                  name: chapterName,
                  chapter: chapterNumber,
                  part: section.Part,
                  readTime: sectionReadTime,
                  sections: [
                    {
                      name: sectionName,
                      section: sectionNumber,
                      html: sectionHtml,
                      readTime: sectionReadTime,
                      relatedQuiz: section['Related Quiz'],
                    },
                  ],
                });
              } else {
                const currentChapter = chapters[chapters.length - 1];
                currentChapter.sections.push({
                  name: sectionName,
                  section: sectionNumber,
                  html: sectionHtml,
                  readTime: sectionReadTime,
                  relatedQuiz: section['Related Quiz'],
                });
                currentChapter.readTime += sectionReadTime;
              }

              previousSectionChapter = chapterNumber;
            }

            const coverImageSource = this.getBookImageFilePath(
              book.ID,
              book['Cover Image']?.[0]?.filename,
            );

            return {
              ...book,
              chapters,
              coverImageSource,
              cssSource,
            };
          } catch (bookError) {
            console.log(`Failed to process book ${book.ID}:`, bookError);
            return null;
          }
        }),
      );

      return processedBooks.filter(book => book?.chapters?.length > 0);
    } catch (error) {
      console.log('processBooks (global) error:', error);
      return [];
    }
  },

  getBookCssFilePath(bookID, filename) {
    if (Platform.OS === 'web') {
      const path = `${bookID}/${filename}`;
      const url =
        `https://ppp-temp-files.s3.amazonaws.com/data/ppBooks/${path}`.replace(
          / /g,
          '%20',
        );
      return url;
    } else {
      const cssPath = DataController.getBookCssPath(filename, bookID);
      return cssPath;
    }
  },

  replaceBlurbImageSrcs(html, bookId) {
    return html.replace(
      /<img([^>]*?)src="([^"]+)"([^>]*?)>/g,
      (match, p1, oldSrc, p2) => {
        const newSrc = this.normalizeImageSrc(oldSrc);
        const finalSrc = this.getBookImageFilePath(bookId, newSrc);
        return `<img${p1} class='bookImage' src="${finalSrc}"${p2}>`;
      },
    );
  },

  async replaceContentsImageSrcs(html, bookId) {
    const matches = [...html.matchAll(/<img([^>]*?)src="([^"]+)"([^>]*?)>/g)];
    let updatedHtml = html;

    for (const match of matches) {
      const [fullMatch, p1, oldSrc, p2] = match;

      const newSrc = this.normalizeImageSrc(oldSrc);
      // ios has to be converted to base64 when using a webview. something to do with WebView file permissions
      const finalSrc =
        Platform.OS === 'ios'
          ? await this.getBookImageAsBase64(bookId, newSrc)
          : this.getBookImageFilePath(bookId, newSrc);

      const replacement = `<img${p1} class='bookImage' src="${finalSrc}"${p2}>`;

      updatedHtml = updatedHtml.replace(fullMatch, replacement);
    }

    return updatedHtml;
  },

  getImageFileType(filename) {
    return filename.split('.').pop();
  },

  async getBookImageAsBase64(bookID, filename) {
    const filePath = DataController.getBookImagePath(filename, bookID);
    const base64 = await DataController.getBase64ImageStringForImagePath(
      filePath,
    );

    const fileType = this.getImageFileType(filename);

    return `data:image/${fileType};base64,${base64}`;
  },

  normalizeImageSrc(src) {
    // Case 1: Local relative path like "../Images/p0011.jpg" or "../images/p0011.jpg"
    if (
      src.startsWith('../Images/') ||
      src.startsWith('../images/') ||
      src.startsWith('images/') ||
      src.startsWith('Images/')
    ) {
      return src.split('/').pop(); // Extract just the file name
    }

    // Case 2: Full URL like "https://pp-book.s3.amazonaws.com/decisionMakingThirdEdition-fig2.3.jpg"
    if (src.startsWith('https://') || src.startsWith('http://')) {
      return src.split('/').pop(); // Extract just the file name
    }

    // Case 3: Already the desired result, just return the src if it's already a valid file name
    return src;
  },

  getBooksForUser(booksIndex, user) {
    const booksForUserTrust = booksIndex
      .filter(book => intersection(user.paraPassTrusts, book.Trusts).length > 0)
      .filter(book => book?.['Table Name'] !== null);

    const booksForUser = booksForUserTrust.filter(book => {
      if (book?.['Status'] === 'Disabled') return false;
      if (book?.['Status'] === 'Testing') return user?.isPPPTester;
      if (book?.['Status'] === 'Live') return true;
    });

    return orderBy(booksForUser, ['Order'], ['asc']);
  },

  async getLocalBooks(user) {
    try {
      const currentBooksIndex = await DataController.getBooksIndexFile();
      const booksForUser = this.getBooksForUser(currentBooksIndex, user);
      const processedBooks = await this.processBooks(booksForUser);
      return processedBooks;
    } catch (err) {
      console.error('Error fetching local books:', err);
      return [];
    }
  },
};
